'use client'

/**
 * WallOfLoveSection — Phase 3 section kind `wall_of_love`.
 *
 * A full "dump" of the tenant's reviews: newest → oldest, every visible
 * review, revealed PROGRESSIVELY as the visitor scrolls (initial batch +
 * IntersectionObserver-driven load-more, with a "Show more" button
 * fallback for no-IO / no-JS-hydration). 3-col CSS-columns masonry.
 *
 * Credibility treatment (mirrors the Endorsal wall): each card shows a
 * large circular reviewer photo, bold name, gold star rating, the review,
 * and a "Google review" source row (G mark + label) at the bottom. The
 * section footer links out to the business's Google reviews so a visitor
 * can verify them. Profiles are NOT linked per-reviewer — Google
 * doesn't expose that cleanly and Endorsal itself links place-level.
 *
 * The review list is handed in pre-resolved from `resolveSectionData`, which
 * has already applied `data.max` ('all' by default). This component never
 * queries the DB and never re-limits: it governs only how many of the given
 * list are PAINTED at a time. That split is deliberate — a second cap here
 * is invisible from the editor, which is exactly how the wall shipped
 * truncated for months (see `wallLimit` in reviewsCore.ts, and the
 * `MARQUEE_MAX = 20` this file used to hold).
 *
 * Scale note: the resolver fetches all matching rows (safety-capped at 500).
 * At pilot scale (dozens–low-hundreds per dealer) that initial payload is
 * trivial. If a dealer ever accumulates many hundreds+, switch the
 * resolver to cursor pagination behind a route handler and feed pages in.
 *
 * Empty state — playful "Be the first" prompt with dashed-border card.
 *
 * Surface: this section used to paint its own light band unconditionally and
 * reset `--section-on-surface*` at its boundary, which made the `style` block
 * in its schema completely inert — choosing a brand background in the editor
 * did nothing. It now falls through: `var(--section-surface, ...)`, so an
 * unset style renders exactly as before and a set one is honored.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import type { ReviewCardData } from './reviewTypes'
import { StarRating, ReviewerAvatar, SourceBadge, AggregateSummary, GoogleMark, ReviewCtaButton, formatReviewDate } from '../reviews'
import { ReviewMarquee } from './ReviewMarquee'
import { eyebrowSurface, sectionEyebrowStyle, sectionHeadlineStyle } from '../../theme/sectionTypography'

export type WallOfLoveSectionData = {
  /** Heading override; defaults to "From our guests". */
  heading?: string
  /**
   * Eyebrow kicker above the heading. Absent → the default "Wall of Love".
   * An empty string hides the line entirely (how a `centered` header is
   * made to match `reviews_summary`, whose band has no eyebrow).
   */
  eyebrow?: string
  layout?: 'masonry_3col' | 'mosaic_2col' | 'scroll_marquee'
  /** Header arrangement. `split` (default) = eyebrow + heading left, rating
   *  aside right. `centered` = a centered stack matching reviews_summary —
   *  which has no eyebrow, so an exact match also needs `eyebrow: ''`. */
  header_layout?: 'split' | 'centered'
  source?: 'featured' | 'top_rated' | 'recent'
  /** How many reviews to show; `'all'` (default) means every one the
   *  `source` slice matches. Applied by the resolver, not here. */
  max?: number | 'all'
  /** Show the aggregate header (stars + average + count). Default true. */
  show_summary?: boolean
  /** Show a prominent "Leave a review" button in the header. Default false. */
  show_write_review?: boolean
}

export type WallOfLoveSectionProps = {
  data: WallOfLoveSectionData
  reviews: ReviewCardData[]
  /** Tenant-wide aggregate (average rating + total count) for the header. */
  aggregate?: { average: number; count: number }
  /** External "leave a review" URL — typically tenant.google_review_url. */
  leaveReviewUrl?: string | null
  /**
   * Live path to the tenant's reviews page, for the `scroll_marquee`
   * layout's "More reviews" link. Resolver-derived (honors a
   * `content_paths` rename); absent → the marquee omits the link.
   */
  moreReviewsHref?: string | null
  /**
   * The dispatcher's authoritative band-darkness flag. Drives the eyebrow
   * treatment, which deliberately does NOT consult `--section-on-surface-muted`
   * (see sectionTypography.ts — letting that override win is what greyed out
   * half the eyebrows on the page). Always the dispatcher's value, never
   * re-derived locally: one source of truth is what keeps the band background
   * and its foreground contrast from disagreeing.
   */
  onDark?: boolean
}

/** Reveal this many on first paint, and per scroll/click batch. */
const BATCH = 12
/** Below this, the marquee loop is too short to read calm — fall back to masonry. */
const MIN_MARQUEE = 8

/**
 * Turn a Google "write a review" link into the matching "see all reviews"
 * link. No-op for any other URL shape (g.page shortlinks, etc.), which
 * fall back to whatever the dealer set.
 */
function toGoogleReviewsUrl(leaveUrl?: string | null): string | null {
  if (!leaveUrl) return null
  return leaveUrl.replace('/local/writereview', '/local/reviews')
}

function EmptyState({
  heading,
  eyebrowText,
  leaveReviewUrl,
  onDark,
}: {
  heading: string
  eyebrowText: string
  leaveReviewUrl?: string | null
  onDark?: boolean
}) {
  return (
    <section
      className="py-[var(--space-section)] px-4 text-center"
      style={{ background: 'var(--section-surface, var(--color-bg-secondary))' }}
      data-component="WallOfLoveSection"
      data-state="empty"
    >
      {eyebrowText && (
        <div className="mb-2" style={sectionEyebrowStyle(eyebrowSurface(onDark))}>
          {eyebrowText}
        </div>
      )}
      <h2
        className="mb-8"
        style={{
          fontFamily: 'var(--font-heading)',
          fontWeight: 'var(--brand-font-heading-weight, 700)' as CSSProperties['fontWeight'],
          ...sectionHeadlineStyle('var(--section-on-surface, var(--color-text-primary))'),
        }}
      >
        {heading}
      </h2>
      {/* The dashed card keeps its own white surface on every band, so its
          body copy stays on a page token — no contrast reasoning needed. */}
      <div className="bg-white border-2 border-dashed border-[var(--color-border-emphasis)] rounded-2xl px-8 py-16 max-w-2xl mx-auto">
        <div className="text-5xl mb-4" aria-hidden="true">💙</div>
        <p className="text-[var(--color-text-secondary)] leading-relaxed mb-6">
          No reviews yet — but our guests have been having a blast on the water. Be the first
          to share your experience.
        </p>
        {leaveReviewUrl && (
          <ReviewCtaButton href={leaveReviewUrl} label="Leave a review on Google" size="lg" />
        )}
      </div>
    </section>
  )
}

function ReviewCard({ r }: { r: ReviewCardData }) {
  const dateLabel = r.review_date ? formatReviewDate(r.review_date) : ''
  return (
    <article className="bg-white rounded-2xl p-6 shadow-[var(--shadow-card-soft)] break-inside-avoid mb-6 inline-block w-full">
      <div className="flex items-center gap-4 mb-5">
        <ReviewerAvatar src={r.reviewer_avatar} name={r.reviewer_name} />
        <div className="min-w-0">
          <div
            className="text-xl md:text-2xl font-bold text-[var(--color-text-primary)] leading-tight"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            {r.reviewer_name}
          </div>
          <div className="flex items-center gap-2 mt-1.5">
            <StarRating rating={r.rating} className="text-xl" />
            {r.review_date && dateLabel && (
              <time dateTime={r.review_date} className="text-sm text-[var(--color-text-secondary)]">
                {dateLabel}
              </time>
            )}
          </div>
        </div>
      </div>
      <p className="text-base leading-relaxed text-[var(--color-text-primary)] m-0">
        {r.review_text}
      </p>
      {r.source_platform && <SourceBadge source={r.source_platform} variant="row" />}
    </article>
  )
}

export function WallOfLoveSection({
  data,
  reviews,
  aggregate,
  leaveReviewUrl,
  moreReviewsHref,
  onDark,
}: WallOfLoveSectionProps) {
  const heading = data.heading || 'From our guests'
  const eyebrowText = data.eyebrow ?? 'Wall of Love'
  const layout = data.layout ?? 'masonry_3col'
  const headerLayout = data.header_layout ?? 'split'
  // mosaic_2col = 2 wider columns with more breathing room; masonry_3col =
  // the default 3-column wall. scroll_marquee is a later (interactive) plan
  // — it falls through to the masonry grid here until then.
  const columnsClass =
    layout === 'mosaic_2col'
      ? 'columns-1 md:columns-2 gap-8'
      : 'columns-1 md:columns-2 lg:columns-3 gap-6'

  // Progressive reveal: paint BATCH on first render (SSR-safe), reveal
  // more as the sentinel scrolls into view or the visitor clicks "Show
  // more". Initial count is deterministic so SSR and first client render
  // match (no hydration mismatch).
  const [visibleCount, setVisibleCount] = useState(BATCH)
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  const hasMore = visibleCount < reviews.length
  const revealMore = useCallback(
    () => setVisibleCount((c) => Math.min(c + BATCH, reviews.length)),
    [reviews.length],
  )

  useEffect(() => {
    if (!hasMore) return
    const node = sentinelRef.current
    if (!node || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) revealMore()
      },
      { rootMargin: '400px 0px' },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [hasMore, visibleCount, revealMore])

  if (reviews.length === 0)
    return (
      <EmptyState
        heading={heading}
        eyebrowText={eyebrowText}
        leaveReviewUrl={leaveReviewUrl}
        onDark={onDark}
      />
    )

  const items = reviews.slice(0, visibleCount)
  const googleReviewsUrl = toGoogleReviewsUrl(leaveReviewUrl)

  // Header config (defaults mirror the schema: summary on, button off).
  const showSummary = (data.show_summary ?? true) && !!aggregate && aggregate.count > 0
  const showWriteReview = (data.show_write_review ?? false) && !!leaveReviewUrl
  const hasHeaderAside = showSummary || showWriteReview

  const eyebrowEl = eyebrowText ? (
    <div style={sectionEyebrowStyle(eyebrowSurface(onDark))}>{eyebrowText}</div>
  ) : null
  const headingEl = (
    <h2
      className="m-0"
      style={{
        fontFamily: 'var(--font-heading)',
        fontWeight: 'var(--brand-font-heading-weight, 700)' as CSSProperties['fontWeight'],
        ...sectionHeadlineStyle('var(--section-on-surface, var(--color-text-primary))'),
      }}
    >
      {heading}
    </h2>
  )
  const ctaEl = showWriteReview ? (
    <ReviewCtaButton href={leaveReviewUrl as string} label="Leave a review" />
  ) : null

  return (
    <section
      className="py-[var(--space-section)] px-4"
      style={{ background: 'var(--section-surface, var(--color-bg-secondary))' }}
      data-component="WallOfLoveSection"
    >
      <div className="max-w-[var(--section-max-width,var(--max-width-page))] mx-auto">
        {headerLayout === 'centered' ? (
          <div className="mb-8 flex flex-col items-center gap-4 text-center" data-header="centered">
            <div className="flex flex-col items-center gap-2">
              {eyebrowEl}
              {headingEl}
            </div>
            {showSummary && (
              <AggregateSummary
                average={aggregate.average}
                count={aggregate.count}
                className="flex flex-col items-center gap-1"
                starsClassName="text-xl"
              />
            )}
            {ctaEl}
          </div>
        ) : (
          <div
            className="mb-8 flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between"
            data-header="split"
          >
            <div className="flex flex-col gap-2">
              {eyebrowEl}
              {headingEl}
            </div>
            {hasHeaderAside && (
              <div className="flex flex-col gap-3 sm:items-end">
                {showSummary && (
                  <AggregateSummary
                    average={aggregate.average}
                    count={aggregate.count}
                    className="flex flex-col gap-1 sm:items-end"
                    starsClassName="text-lg"
                  />
                )}
                {ctaEl}
              </div>
            )}
          </div>
        )}
        {layout === 'scroll_marquee' && reviews.length >= MIN_MARQUEE ? (
          <ReviewMarquee reviews={reviews} moreReviewsHref={moreReviewsHref} />
        ) : (
          <div className={columnsClass}>
            {items.map((r) => (
              <ReviewCard key={r.id} r={r} />
            ))}
          </div>
        )}
        {hasMore && (
          <div ref={sentinelRef} className="mt-8 flex justify-center">
            <button
              type="button"
              onClick={revealMore}
              className="inline-block px-7 py-3 rounded-lg font-semibold border transition-opacity hover:opacity-80"
              style={{
                background: 'transparent',
                color: 'var(--section-on-surface, var(--color-text-primary))',
                borderColor: 'currentColor',
              }}
            >
              Show more reviews
            </button>
          </div>
        )}
        {googleReviewsUrl && (
          <div className="mt-8 flex justify-center">
            <a
              href={googleReviewsUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 text-sm font-medium transition-opacity hover:opacity-80"
              style={{ color: 'var(--section-on-surface-muted, var(--color-text-secondary))' }}
            >
              <GoogleMark className="size-4" />
              See all our reviews on Google
            </a>
          </div>
        )}
      </div>
    </section>
  )
}
